import { forwardRef, useMemo, useState, type HTMLAttributes } from "react";
import { Button } from "./button.js";
import { Chip } from "./chip.js";
import { RadioGroup, RadioGroupItem } from "./radio-group.js";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "./select.js";
import { ToolCard } from "./tool-card.js";
import { cn } from "./lib/cn.js";
import {
 TOOL_NAMES_ORDERED,
 ToolKind,
 getToolSpec,
 toolKindLabel,
} from "./lib/tool-catalog.js";

/**
 * The draft loadout — what a Mech is built from. Matches the YAML
 * shape `agents/*.yaml::draft.picks`: an ordered list of tools, each
 * with an optional `charges` override (only meaningful for tools
 * that aren't already unlimited). The order matters for the resolver's
 * mount alternation per `server/mecharena/mount.py`.
 *
 * Hard-coded engine constants from `server/mecharena/draft.py`:
 * - POWER_CAP = 24 power across all picks
 * - PICKS_REQUIRED = 8 slots (fewer is legal; fill-up tops with SMALL_LASER)
 */
export interface LoadoutPick {
 tool: string;
 /** Override the catalog charges value. Leave undefined for the default. */
 charges?: number;
}

export type LoadoutStrategy = "static" | "pack-flow";

export interface LoadoutBuilderProps
 extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
 value: LoadoutPick[];
 onChange: (picks: LoadoutPick[]) => void;
 /** Static = these exact picks; pack-flow = these are preferences,
 * agent drafts from procedurally generated 5-card packs. */
 strategy?: LoadoutStrategy;
 onStrategyChange?: (strategy: LoadoutStrategy) => void;
 /** Default 8 per PICKS_REQUIRED. */
 maxSlots?: number;
 /** Default 24 per POWER_CAP. */
 powerCap?: number;
 /** Pass `true` to hide the strategy radio (e.g. when the parent owns
 * it via a higher-level form). */
 hideStrategyToggle?: boolean;
}

interface ValidationProblem {
 slotIndex: number;
 kind: "missing-prereq" | "duplicate-prereq-orphan";
 message: string;
}

/**
 * Validates the loadout against the engine's draft rules.
 *
 * - **missing-prereq**: a tool's prerequisites must also be in the
 * loadout. Example: JUMP requires JUMP_JETS.
 * - **duplicate-prereq-orphan**: JUMP_JETS without JUMP is dead
 * weight (allowed by the engine, but the linter flags it as a
 * hint).
 */
function validate(picks: LoadoutPick[]): ValidationProblem[] {
 const problems: ValidationProblem[] = [];
 const present = new Set(picks.map((p) => p.tool));
 picks.forEach((pick, i) => {
 const spec = getToolSpec(pick.tool);
 if (!spec) return;
 for (const prereqId of spec.prerequisites) {
 const prereq = getToolSpec(prereqId);
 if (!prereq) continue;
 if (!present.has(prereq.name)) {
 problems.push({
 slotIndex: i,
 kind: "missing-prereq",
 message: `${spec.name} requires ${prereq.name}`,
 });
 }
 }
 });
 return problems;
}

function powerUsed(picks: LoadoutPick[]): number {
 return picks.reduce((acc, p) => acc + (getToolSpec(p.tool)?.powerCost ?? 0), 0);
}

export const LoadoutBuilder = forwardRef<HTMLDivElement, LoadoutBuilderProps>(
 (
 {
 value,
 onChange,
 strategy = "static",
 onStrategyChange,
 maxSlots = 8,
 powerCap = 24,
 hideStrategyToggle = false,
 className,
 ...rest
 },
 ref,
 ) => {
 const [picker, setPicker] = useState<string>("");

 const problems = useMemo(() => validate(value), [value]);
 const used = powerUsed(value);
 const overBudget = used > powerCap;
 const pctRaw = (used / powerCap) * 100;
 const pct = Math.min(100, pctRaw);

 const remove = (idx: number) => {
 onChange(value.filter((_, i) => i !== idx));
 };

 const move = (idx: number, direction: -1 | 1) => {
 const next = [...value];
 const target = idx + direction;
 if (target < 0 || target >= next.length) return;
 [next[idx], next[target]] = [next[target]!, next[idx]!];
 onChange(next);
 };

 const add = () => {
 if (!picker) return;
 if (value.length >= maxSlots) return;
 onChange([...value, { tool: picker }]);
 setPicker("");
 };

 const problemsBySlot = useMemo(() => {
 const map = new Map<number, ValidationProblem[]>();
 for (const p of problems) {
 const list = map.get(p.slotIndex) ?? [];
 list.push(p);
 map.set(p.slotIndex, list);
 }
 return map;
 }, [problems]);

 return (
 <div
 ref={ref}
 className={cn("flex flex-col gap-4", className)}
 {...rest}
 >
 {!hideStrategyToggle && (
 <fieldset className="flex flex-col gap-2">
 <legend className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 Draft strategy
 </legend>
 <RadioGroup
 value={strategy}
 onValueChange={(v) =>
 onStrategyChange?.(v as LoadoutStrategy)
 }
 className="flex gap-4"
 >
 <label className="flex items-center gap-2 text-[var(--pp-font-md)]">
 <RadioGroupItem value="static" />
 Static — these exact 8 picks
 </label>
 <label className="flex items-center gap-2 text-[var(--pp-font-md)]">
 <RadioGroupItem value="pack-flow" />
 Pack-flow — agent drafts from packs
 </label>
 </RadioGroup>
 </fieldset>
 )}

 <PowerMeter used={used} cap={powerCap} pct={pct} over={overBudget} />

 <ol className="flex flex-col gap-2" aria-label="Loadout slots">
 {value.map((pick, idx) => {
 const spec = getToolSpec(pick.tool);
 const slotProblems = problemsBySlot.get(idx) ?? [];
 return (
 <li
 key={`${pick.tool}-${idx}`}
 className={cn(
 "flex flex-wrap items-center gap-2 border p-2",
 slotProblems.length > 0
 ? "border-[var(--pp-danger)]"
 : "border-[var(--pp-border)]",
 )}
 >
 <span className="w-6 font-[var(--pp-font-family)] text-[var(--pp-font-sm)] tabular-nums text-[var(--pp-text-muted)]">
 {idx + 1}.
 </span>
 <ToolCard tool={pick.tool} charges={pick.charges} />
 {spec && (
 <Chip
 tone="muted"
 aria-label={`${spec.powerCost} power`}
 >
 {spec.powerCost}P
 </Chip>
 )}
 {slotProblems.map((p) => (
 <Chip key={p.message} tone="error">
 ! {p.message}
 </Chip>
 ))}
 <span className="ml-auto flex items-center gap-1">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => move(idx, -1)}
 disabled={idx === 0}
 aria-label={`Move ${pick.tool} up`}
 >
 ▲
 </Button>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => move(idx, 1)}
 disabled={idx === value.length - 1}
 aria-label={`Move ${pick.tool} down`}
 >
 ▼
 </Button>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => remove(idx)}
 aria-label={`Remove ${pick.tool}`}
 >
 ✕
 </Button>
 </span>
 </li>
 );
 })}
 {value.length === 0 && (
 <li className=" border border-dashed border-[var(--pp-border)] p-4 text-center text-[var(--pp-font-md)] text-[var(--pp-text-muted)]">
 No picks yet. Add tools below.
 </li>
 )}
 </ol>

 {value.length < maxSlots && (
 <div className="flex items-center gap-2">
 <div className="flex-1">
 <Select value={picker} onValueChange={setPicker}>
 <SelectTrigger aria-label="Pick a tool to add">
 <SelectValue placeholder="Pick a tool to add…" />
 </SelectTrigger>
 <SelectContent>
 {(["MOVEMENT", "DIRECT_FIRE", "MISSILE", "MELEE", "REACTIVE", "PASSIVE", "SENSOR", "HEAT_UTILITY"] as const).map(
 (kindName) => {
 const kind = ToolKind[kindName];
 const tools = TOOL_NAMES_ORDERED.filter(
 (n) => getToolSpec(n)?.kind === kind,
 );
 if (tools.length === 0) return null;
 return (
 <SelectGroupSection
 key={kindName}
 label={toolKindLabel(kind)}
 tools={tools}
 />
 );
 },
 )}
 </SelectContent>
 </Select>
 </div>
 <Button onClick={add} disabled={!picker}>
 + Add tool
 </Button>
 </div>
 )}

 <footer className="flex flex-wrap items-center justify-between gap-2 text-[var(--pp-font-sm)] font-[var(--pp-font-family)] text-[var(--pp-text-muted)]">
 <span>
 {value.length} / {maxSlots} slots
 </span>
 {problems.length > 0 && (
 <span className="text-[var(--pp-danger)]">
 {problems.length} validation {problems.length === 1 ? "issue" : "issues"}
 </span>
 )}
 </footer>
 </div>
 );
 },
);
LoadoutBuilder.displayName = "LoadoutBuilder";

function SelectGroupSection({
 label,
 tools,
}: {
 label: string;
 tools: readonly string[];
}) {
 return (
 <>
 <div className="px-2 py-1 font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 {label}
 </div>
 {tools.map((name) => (
 <SelectItem key={name} value={name}>
 {name}
 </SelectItem>
 ))}
 </>
 );
}

function PowerMeter({
 used,
 cap,
 pct,
 over,
}: {
 used: number;
 cap: number;
 pct: number;
 over: boolean;
}) {
 return (
 <div className="flex flex-col gap-1">
 <div className="flex items-center justify-between font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)]">
 <span className="text-[var(--pp-text-muted)]">Power budget</span>
 <span
 className={cn(
 "tabular-nums",
 over ? "text-[var(--pp-danger)]" : "text-[var(--pp-text)]",
 )}
 role="status"
 aria-live="polite"
 >
 {used} / {cap}
 </span>
 </div>
 <div
 className="h-2 w-full overflow-hidden border border-[var(--pp-border)]"
 role="progressbar"
 aria-valuenow={used}
 aria-valuemax={cap}
 aria-label="Power budget"
 >
 <span
 className={cn(
 "block h-full transition-[width]",
 over ? "bg-[var(--pp-danger)]" : "bg-[var(--accent)]",
 )}
 style={{ width: `${pct}%` }}
 />
 </div>
 {over && (
 <span className="text-[var(--pp-font-sm)] text-[var(--pp-danger)]">
 Over budget — drop {used - cap} power to be legal.
 </span>
 )}
 </div>
 );
}
